package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import dev.lingvo.shared.ProposalPageDto
import dev.lingvo.shared.ReadinessDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.nav.AppRoute
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch

internal val LocalAppShellProjectReads = staticCompositionLocalOf<AppShellProjectReads?> { null }

private data class ShellReadOwner(val subject: ProjectAuthoritySubject, val credential: String)
private data class ShellReadAttempt(
    val owner: ShellReadOwner,
    val epoch: Long,
    val routeEpoch: Long,
    val order: Long,
    val browserRoute: String,
)

/** Count publication only: page loaders still own their responses, failures and deliberate retries. */
internal class AppShellProjectReads {
    private var owner: ShellReadOwner? = null
    private var path: String? = null
    private var routeEpoch = 0L
    private var epoch = 0L
    private var proposalOrder = 0L
    private var readinessOrder = 0L
    private var proposalTotal by mutableStateOf<Int?>(null)
    private var qaErrors by mutableStateOf<Int?>(null)

    val proposals: Int? get() = proposalTotal.takeIf { owner?.let(::owns) == true }
    val qa: Int? get() = qaErrors.takeIf { owner?.let(::owns) == true }

    fun bind(subject: ProjectAuthoritySubject?, credential: String?, currentPath: String) {
        val next = subject?.let { exact -> credential?.takeIf(String::isNotBlank)?.let { ShellReadOwner(exact, it) } }
        if (path != currentPath) {
            path = currentPath
            routeEpoch++
        }
        if (owner == next) return
        owner = next
        epoch++
        proposalOrder++
        readinessOrder++
        proposalTotal = null
        qaErrors = null
    }

    /** Called only after the page's current guarded action is acknowledged. Never starts a request. */
    fun invalidateProposals(subject: ProjectAuthoritySubject) {
        if (owner?.let { it.subject == subject && owns(it) } != true) return
        epoch++
        proposalOrder++
        proposalTotal = null
    }

    /** Wraps the existing unfiltered page GET; there is no cache, subtraction or additional fetch. */
    suspend fun observeProposals(subject: ProjectAuthoritySubject, load: suspend () -> ProposalPageDto): ProposalPageDto {
        val read = owner?.takeIf { it.subject == subject && owns(it) }
            ?.let { ShellReadAttempt(it, epoch, routeEpoch, ++proposalOrder, browserRoute()) }
        val response = load()
        if (read != null && read.epoch == epoch && read.routeEpoch == routeEpoch &&
            read.order == proposalOrder && read.browserRoute == browserRoute() && owns(read.owner)
        ) proposalTotal = response.total.takeIf { it >= 0 }
        return response
    }

    suspend fun observeReadiness(subject: ProjectAuthoritySubject, load: suspend () -> ReadinessDto): ReadinessDto {
        val read = owner?.takeIf { it.subject == subject && owns(it) }
            ?.let { ShellReadAttempt(it, epoch, routeEpoch, ++readinessOrder, browserRoute()) }
        val response = load()
        if (read != null && read.epoch == epoch && read.routeEpoch == routeEpoch &&
            read.order == readinessOrder && read.browserRoute == browserRoute() && owns(read.owner) &&
            response.projectSlug == subject.action.routeSlug
        ) qaErrors = response.locales.sumOf { it.qaErrors }.takeIf { it >= 0 }
        return response
    }

    private fun owns(expected: ShellReadOwner): Boolean =
        owner == expected && Session.token == expected.credential &&
            AppRoute.projectSlug(window.location.pathname) == expected.subject.action.routeSlug &&
            liveProjectRoute(expected.subject.action.routeSlug)?.authoritySubject(Session.organizations) == expected.subject

    private fun browserRoute(): String = window.location.pathname + window.location.search + window.location.hash
}

/** Initial shell reads run once per exact project/member/credential, not once per desktop/drawer row. */
@Composable
internal fun rememberAppShellProjectReads(currentPath: String): AppShellProjectReads {
    val reads = remember { AppShellProjectReads() }
    val subject = AppRoute.projectSlug(currentPath)
        ?.let(::liveProjectRoute)?.authoritySubject(Session.organizations)
    val credential = Session.token
    reads.bind(subject, credential, currentPath)
    LaunchedEffect(subject, credential) {
        val exact = subject ?: return@LaunchedEffect
        coroutineScope {
            launch {
                try {
                    reads.observeReadiness(exact) {
                        requestProjectResource(exact.action, exact.action.routeSlug) { route ->
                            ApiClient.readiness(route.project.slug)
                        }
                    }
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (_: Throwable) {
                    // A standalone count failure remains unknown; the shell adds no global warning.
                }
            }
            launch {
                try {
                    reads.observeProposals(exact) {
                        requestProjectResource(exact.action, exact.action.routeSlug) { route ->
                            ApiClient.proposals(route.project.slug)
                        }
                    }
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (_: Throwable) {
                    // The proposal page owns its existing read-failure and retry surface.
                }
            }
        }
    }
    return reads
}
